import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { API, api, fileUrl } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";
import ImageCarousel from "@/components/ImageCarousel";
import BulletText from "@/components/BulletText";
import CustomerOnboarding from "@/components/CustomerOnboarding";
import { getCustomerSession, isLiked, toggleLikedLocally } from "@/lib/showroomSession";
import { ChevronLeft, Sparkles, Download, ChevronDown, Heart, Loader2 } from "lucide-react";
import { errorDetail } from "@/lib/utils";
import { TOKEN_KEY } from "@/lib/platform";

export default function PublicProduct() {
  const { code } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [needsSession, setNeedsSession] = useState(false);

  const fetchProduct = React.useCallback(() => {
    setErr("");
    api
      .get(`/public/products/${code}`)
      .then((r) => setP(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          setNeedsSession(true);
        } else {
          setErr(errorDetail(e) || "Product not found");
        }
      });
  }, [code]);

  useEffect(() => {
    // Only fetch if a session (staff or customer) exists.
    const token = localStorage.getItem(TOKEN_KEY);
    const session = getCustomerSession();
    if (!token && !session) {
      setNeedsSession(true);
      return;
    }
    fetchProduct();
  }, [code, fetchProduct]);

  // Re-fetch whenever a customer session is started elsewhere.
  useEffect(() => {
    const handler = () => {
      setNeedsSession(false);
      fetchProduct();
    };
    window.addEventListener("heda-customer-session-changed", handler);
    return () => window.removeEventListener("heda-customer-session-changed", handler);
  }, [fetchProduct]);

  // Like / Unlike handler — logs event AND updates local session state.
  const onToggleLike = async () => {
    if (!p) return;
    const currently = isLiked(p.id);
    const newType = currently ? "unlike" : "like";
    toggleLikedLocally(p.id, !currently);
    try {
      await api.post("/showroom/event", {
        type: newType,
        product_id: p.id,
        product_code: p.code,
        product_name: p.name,
      });
    } catch {
      /* network may be down; like state stays local */
    }
  };

  if (needsSession) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-md px-4 py-12 md:py-16">
          <div className="heda-panel p-7 text-center sm:p-8">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-gold-tint">
              <Sparkles size={26} className="text-heda-gold" />
            </div>
            <div className="hed-overline">Private catalog</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink">
              Please ask the showroom staff.
            </h1>
            <p className="mt-2 text-sm text-heda-sub">
            The Heda catalog is private. A staff member can start a session for you in seconds —
            they&rsquo;ll just need your name and mobile number so we can show you what suits you best.
            </p>
          </div>
        </div>
        <CustomerOnboarding open={false} onClose={() => {}} />
      </PublicLayout>
    );
  }

  if (err) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-md px-4 py-12 md:py-16">
          <div className="heda-panel p-7 text-center sm:p-8">
            <div className="hed-overline">Not found</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink">This product page isn&rsquo;t available.</h1>
            <p className="mt-2 text-sm text-heda-sub">{err}</p>
            <Link to="/search" className="heda-btn mt-6">
              <ChevronLeft size={14} /> Browse products
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!p) {
    return (
      <PublicLayout>
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-16 text-sm text-heda-mute md:px-8">
          <Loader2 size={18} className="animate-spin text-heda-gold" /> Loading...
        </div>
      </PublicLayout>
    );
  }

  const images = p.images || [];
  const approved = p.approved_suggestions || {};

  // Smart size/dimensions consolidation:
  // If we have available_sizes, that IS the dimensions info — show one combined "Sizes" line.
  const primarySize =
    p.available_sizes?.[0] || p.dimensions || null;
  const extraSizes = p.available_sizes && p.available_sizes.length > 1
    ? p.available_sizes.slice(1).join(", ")
    : null;

  return (
    <PublicLayout>
      <article className="mx-auto w-full min-w-0 max-w-7xl px-4 py-5 md:px-8 md:py-10">
        {/* Hero grid */}
        <div className="grid gap-6 md:gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Gallery */}
          <div className="animate-fade-up">
            <ImageCarousel images={images} productName={p.name} />
          </div>

          {/* Title + key info */}
          <div className="animate-fade-up">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {p.category && <span className="hed-overline text-heda-gold">{p.category}</span>}
              {p.material && (
                <>
                  <span className="text-heda-line">·</span>
                  <span className="hed-overline">{p.material}</span>
                </>
              )}
            </div>
            <h1 className="break-words font-display text-[30px] font-bold leading-[1.1] text-heda-ink sm:text-4xl md:text-5xl">
              {p.name}
            </h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="text-[11.5px] tracking-wider text-heda-mute tabular-nums">
                CODE · {p.code}
              </div>
              {getCustomerSession() && (
                <button
                  onClick={onToggleLike}
                  data-testid="public-like-button"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition ${
                    isLiked(p.id)
                      ? "border-red-700 bg-red-700 text-white hover:bg-red-800"
                      : "border-heda-line bg-white text-heda-ink hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                  }`}
                >
                  <Heart size={13} className={isLiked(p.id) ? "fill-white" : ""} />
                  {isLiked(p.id) ? "Liked" : "Like"}
                </button>
              )}
            </div>

            {p.description && (
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-heda-sub">
                {p.description}
              </p>
            )}

            {p.applications?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {p.applications.slice(0, 6).map((a, i) => (
                  <span
                    key={i}
                    className="hed-chip text-[12px]"
                    data-testid={`application-chip-${i}`}
                  >
                    {a.length > 40 ? a.slice(0, 38) + "…" : a}
                  </span>
                ))}
              </div>
            )}

            {/* Pricing block */}
            {p.prices?.length > 0 && (
              <div className="heda-card mt-6 p-4 md:p-5">
                <div className="hed-overline mb-2">Price</div>
                <div className="space-y-1.5">
                  {p.prices.map((pr, i) => (
                    <div
                      key={i}
                      className="flex items-baseline justify-between gap-3"
                      data-testid={`price-display-${i}`}
                    >
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[13px] text-heda-mute">
                          {pr.currency || "₹"}
                        </span>
                        <span className="font-display text-2xl font-bold tabular-nums text-heda-ink">
                          {Number(pr.value).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        <span className="text-[12px] text-heda-mute">
                          / {unitLabel(pr.unit)}
                        </span>
                      </div>
                      {pr.notes && (
                        <span className="text-[11.5px] text-heda-mute">{pr.notes}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[11.5px] text-heda-mute">
                  Prices indicative · contact us for bulk and trade pricing.
                </div>
              </div>
            )}

            {/* Key info strip — only show fields with values */}
            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-heda-line pt-5 sm:grid-cols-3">
              {p.thickness && <KV label="Thickness" value={p.thickness} />}
              {primarySize && (
                <KV
                  label="Available Size"
                  value={
                    extraSizes ? (
                      <>
                        {primarySize}
                        <div className="mt-0.5 text-[10.5px] font-medium text-heda-mute">
                          +{extraSizes}
                        </div>
                      </>
                    ) : (
                      primarySize
                    )
                  }
                />
              )}
              {p.material && <KV label="Material" value={p.material} />}
            </div>

            <div className="mt-7">
              <button
                className="heda-btn-primary w-full sm:w-auto"
                onClick={() => {
                  document
                    .getElementById("specifications")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="view-specs-button"
              >
                View full specifications
                <ChevronDown size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* Long-form sections */}
        <div
          className="mt-10 grid gap-6 md:mt-14 lg:grid-cols-3 lg:gap-8"
          id="specifications"
        >
          <div className="heda-panel min-w-0 space-y-10 p-5 md:p-7 lg:col-span-2">
            {p.specifications?.length > 0 && (
              <section>
                <div className="hed-overline">Specifications</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  The exact numbers.
                </h2>
                <div className="border-t border-heda-line">
                  {p.specifications.map((s, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-3 gap-4 border-b border-heda-line py-3 text-sm"
                    >
                      <div className="break-words text-heda-sub">{s.key}</div>
                      <div className="col-span-2 break-words font-semibold text-heda-ink">
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {p.variants?.length > 0 && (
              <section data-testid="public-variants-section">
                <div className="hed-overline">Profile Variants</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  Choose your profile.
                </h2>
                <div className="heda-card overflow-x-auto">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="heda-thead">
                      <tr>
                        <th className="px-4 py-2.5">Code</th>
                        <th className="px-4 py-2.5">Finish</th>
                        <th className="px-4 py-2.5">Length</th>
                        <th className="px-4 py-2.5">Coverage</th>
                        <th className="px-4 py-2.5">Weight</th>
                        <th className="px-4 py-2.5">Thickness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.variants.map((v, idx) => (
                        <tr
                          key={idx}
                          className="border-t border-heda-line"
                          data-testid={`public-variant-row-${idx}`}
                        >
                          <td className="px-4 py-2.5 font-semibold text-heda-ink">{v.code}</td>
                          <td className="px-4 py-2.5 text-heda-sub">{v.finish || "—"}</td>
                          <td className="px-4 py-2.5 text-heda-sub">{v.length || "—"}</td>
                          <td className="px-4 py-2.5 text-heda-sub">{v.coverage || "—"}</td>
                          <td className="px-4 py-2.5 text-heda-sub">{v.weight || "—"}</td>
                          <td className="px-4 py-2.5 text-heda-sub">{v.thickness || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {p.features?.length > 0 && (
              <section>
                <div className="hed-overline">Features</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  Crafted to perform.
                </h2>
                <ul className="grid gap-x-8 gap-y-2.5 text-[15px] text-heda-ink sm:grid-cols-2">
                  {p.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 min-w-0">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-heda-gold" />
                      <span className="flex-1 min-w-0 break-words">{f}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(p.available_sizes?.length > 0 || p.available_colors?.length > 0) && (
              <section className="grid gap-6 sm:grid-cols-2 md:gap-8">
                {p.available_sizes?.length > 0 && (
                  <div>
                    <div className="hed-overline">Available Sizes</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.available_sizes.map((s, i) => (
                        <span key={i} className="hed-chip text-[12px]">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {p.available_colors?.length > 0 && (
                  <div>
                    <div className="hed-overline">Available Colors</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.available_colors.map((c, i) => (
                        <span key={i} className="hed-chip text-[12px]">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {p.technical_info && (
              <section>
                <div className="hed-overline">Technical Information</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  Good to know.
                </h2>
                <BulletText
                  text={p.technical_info}
                  className="text-[15px] leading-relaxed text-heda-ink"
                />
              </section>
            )}

            {(p.installation_instructions ||
              approved.installation_recommendations?.length > 0) && (
              <section>
                <div className="hed-overline">Installation</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  How it goes in.
                </h2>
                {p.installation_instructions && (
                  <BulletText
                    text={p.installation_instructions}
                    className="text-[15px] leading-relaxed text-heda-ink"
                  />
                )}
                {approved.installation_recommendations?.length > 0 && (
                  <ul className="mt-4 space-y-2 text-[15px] text-heda-ink">
                    {approved.installation_recommendations.map((v, i) => (
                      <li key={i} className="flex items-start gap-2 min-w-0">
                        <Sparkles
                          size={12}
                          className="mt-1.5 flex-shrink-0 text-heda-gold"
                        />
                        <span className="flex-1 min-w-0 break-words">{v}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {approved.recommended_applications?.length > 0 && (
              <section>
                <div className="hed-overline">Recommended Applications</div>
                <ul className="mt-3 grid gap-x-8 gap-y-2 text-[15px] text-heda-ink sm:grid-cols-2">
                  {approved.recommended_applications.map((v, i) => (
                    <li key={i} className="flex items-start gap-2 min-w-0">
                      <Sparkles
                        size={12}
                        className="mt-1.5 flex-shrink-0 text-heda-gold"
                      />
                      <span className="flex-1 min-w-0 break-words">{v}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {approved.maintenance_instructions?.length > 0 && (
              <section>
                <div className="hed-overline">Maintenance</div>
                <ul className="mt-3 space-y-2 text-[15px] text-heda-ink">
                  {approved.maintenance_instructions.map((v, i) => (
                    <li key={i} className="flex items-start gap-2.5 min-w-0">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-heda-gold" />
                      <span className="flex-1 min-w-0 break-words">{v}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {p.research_notes && (
              <section data-testid="public-research-section">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-heda-gold" />
                  <div className="hed-overline">Researched on the web</div>
                </div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  Beyond the brochure.
                </h2>
                <div className="space-y-4 text-[15px] leading-relaxed text-heda-ink">
                  {p.research_notes
                    .split(/\n\s*\n/)
                    .filter((para) => para.trim())
                    .map((para, idx) => {
                      const lines = para.split("\n");
                      const head = lines[0]?.trim();
                      const isHead =
                        head &&
                        /^[A-Z][A-Z &']{3,40}:/.test(head);
                      if (isHead) {
                        const heading = head.replace(/:$/, "");
                        const rest = lines.slice(1).join(" ").trim();
                        return (
                          <div key={idx}>
                            <div className="heda-label text-heda-gold">
                              {heading.replace(/:.*$/, "")}
                            </div>
                            <p className="text-[15px]">{rest || head.replace(/^[A-Z][A-Z &']{3,40}:\s*/, "")}</p>
                          </div>
                        );
                      }
                      return (
                        <p key={idx}>{para.replace(/\s+/g, " ").trim()}</p>
                      );
                    })}
                </div>
                {p.research_sources?.length > 0 && (
                  <div className="mt-5">
                    <div className="hed-overline mb-2">
                      Sources
                    </div>
                    <ul className="space-y-1.5">
                      {p.research_sources.slice(0, 6).map((u, i) => (
                        <li key={i} className="text-xs break-words">
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="text-heda-gold underline underline-offset-2 hover:text-heda-ink"
                            data-testid={`public-research-source-${i}`}
                          >
                            {u}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {p.reference_sheets?.filter((s) => s.include_public).length > 0 && (
              <section>
                <div className="hed-overline">Reference Sheets</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  From the brochure.
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                  {p.reference_sheets
                    .filter((s) => s.include_public)
                    .map((s, i) => (
                      <a
                        key={s.id}
                        href={fileUrl(s.storage_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="block overflow-hidden rounded-xl border border-heda-line bg-white transition hover:border-heda-gold"
                        data-testid={`public-ref-sheet-${i}`}
                      >
                        <div className="aspect-[3/4] bg-heda-cream overflow-hidden">
                          <img
                            src={fileUrl(s.storage_path)}
                            alt={s.title || s.label}
                            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                          />
                        </div>
                        <div className="p-2.5">
                          <div className="hed-overline text-[10px]">
                            {s.label.replace("_", " ")}
                          </div>
                          <div className="mt-0.5 truncate text-xs font-semibold text-heda-ink">{s.title}</div>
                        </div>
                      </a>
                    ))}
                </div>
              </section>
            )}

            {approved.faqs?.length > 0 && (
              <section>
                <div className="hed-overline">Frequently asked</div>
                <h2 className="mb-4 mt-1 font-display text-[20px] font-bold text-heda-ink md:text-2xl">
                  Good questions.
                </h2>
                <div className="divide-y divide-heda-line overflow-hidden rounded-2xl border border-heda-line">
                  {approved.faqs.map((f, idx) => (
                    <details key={idx} className="group bg-white px-4 py-3.5 open:bg-heda-cream/40" data-testid={`faq-item-${idx}`}>
                      <summary className="flex cursor-pointer list-none items-center justify-between text-[15px] font-semibold text-heda-ink">
                        <span>{f.question}</span>
                        <span className="ml-3 text-xl text-heda-gold transition-transform group-open:rotate-45">
                          +
                        </span>
                      </summary>
                      <p className="mt-2 text-[14px] leading-relaxed text-heda-sub">
                        {f.answer}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar: docs */}
          <aside className="min-w-0 space-y-6">
            <div className="heda-card p-5">
              <h3 className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
                <Download size={16} className="text-heda-gold" /> Documents
              </h3>
              {p.files?.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {p.files.map((f) => (
                    <a
                      key={f.id}
                      href={fileUrl(f.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between rounded-xl border border-heda-line px-3 py-2.5 text-sm transition hover:border-heda-gold hover:text-heda-gold"
                      data-testid={`download-doc-${f.id}`}
                    >
                      <span className="flex min-w-0 items-center gap-2 truncate">
                        <Download size={14} className="shrink-0 text-heda-gold" />
                        {f.original_filename}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-[13px] text-heda-mute">No downloadable documents.</p>
              )}
            </div>

            <div className="rounded-2xl border border-heda-gold/30 bg-heda-gold-tint/60 p-5">
              <div className="hed-overline">Heda AI assistant</div>
              <p className="mt-1 font-display text-[20px] font-bold leading-snug text-heda-ink">Got a question?</p>
              <p className="mt-1 text-[13px] text-heda-sub">
                Tap the gold &ldquo;Ask Heda AI&rdquo; button to chat with the assistant trained on this product.
              </p>
            </div>
          </aside>
        </div>
      </article>

      <ChatWidget productCode={p.code} productName={p.name} />
    </PublicLayout>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="hed-overline">{label}</div>
      <div className="mt-1 text-sm font-semibold text-heda-ink">{value || "—"}</div>
    </div>
  );
}

function unitLabel(unit) {
  const map = {
    piece: "piece",
    box: "box",
    sqft: "sq ft",
    sheet: "sheet",
    running_ft: "running ft",
  };
  return map[unit] || unit || "unit";
}
